import Exif from '@fengyuanchen/exif';
import { api } from '../config/constants';

const CORRECT_IMAGE_ORIENTATION = 1;
const MAX_WIDTH = 3200;
const MAX_HEIGHT = 3200;
const UUID_LENGTH = 22;

// iOS Safari caps a canvas backing store at this many pixels (~4096x4096).
// We keep every working canvas at or under this so the resize never crashes
// on iPhone/iPad. Source: https://pqina.nl/blog/canvas-area-exceeds-the-maximum-limit/
const SAFE_CANVAS_AREA_PX = 16777216;

// Max single canvas dimension. Even with area under the cap, an extreme aspect
// ratio (e.g. a 50000x335 panorama) can exceed a browser's per-axis canvas
// limit (Chrome ~32767). 16384 is supported everywhere and far above our
// target. Source: https://github.com/jhildenbiddle/canvas-size
const MAX_CANVAS_DIMENSION_PX = 16384;

// Images above this resolution are rejected rather than resized. NOTE: this is
// a decompression-bomb guard, NOT a guaranteed OOM preventer -- the check runs
// after the browser decodes the bitmap (a ~100MP decode is ~400MB of RGBA and
// could OOM a weak device first). Intentionally generous so it never rejects a
// real photo; the canvas clamps above are what keep the actual resize safe.
const MAX_SOURCE_MEGAPIXELS = 100;

export const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const getImageLink = (imageUUID, imagesLinks, size) => {
  let link;
  if (imageUUID) {
    if (typeof imageUUID === 'object') {
      imageUUID = imageUUID.UUID;
    }
    if (imageUUID.includes('refuuid')) {
      link = imagesLinks[imageUUID.slice(-UUID_LENGTH)]; // from store
    } else {
      link = `${api.GET_IMAGE}/${imageUUID}`;
      if (size) {
        const ratio = window.devicePixelRatio;
        link += `?width=${Math.round(size.width * ratio)}&height=${Math.round(
          size.height * ratio,
        )}`;
      }
    }
  }
  return link;
};

export const isFileImage = (file) => {
  return (
    file &&
    (file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/gif')
  );
};

export const processImageConsideringOrientation = (file, callback) => {
  if (file.type === 'image/jpeg') {
    new Exif(file, {
      interoperability: false,
      gps: false,
      exif: true,
      done: (tags) => {
        if (tags.Orientation !== CORRECT_IMAGE_ORIENTATION) {
          fixImageOrientation(file, tags, callback);
        } else {
          callback(file);
        }
      },
      fail: () => {
        callback(file);
      },
    });
  } else {
    callback(file);
  }
};

export const fixImageOrientation = (file, tags, callback) => {
  const updateResults = (img, data) => {
    img.toBlob(
      (blob) => {
        callback(blob);
      },
      file.type,
      1.0,
    );
  };
  window.loadImage(file, updateResults, {
    canvas: true,
    pixelRatio: window.devicePixelRatio,
    downsamplingRatio: 0.5,
    orientation: true,
  });
};

// Compute target dimensions so the longest edge is <= maxEdge, preserving
// aspect ratio. Pure (no DOM), so it is unit-testable. Never upscales.
export const computeScaledDimensions = (srcWidth, srcHeight, maxEdge) => {
  const longest = Math.max(srcWidth, srcHeight);
  if (longest <= maxEdge) {
    return { width: srcWidth, height: srcHeight, needsResize: false };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(srcWidth * scale)),
    height: Math.max(1, Math.round(srcHeight * scale)),
    needsResize: true,
  };
};

// Scale dimensions down so width*height <= maxArea, preserving aspect ratio.
// Used to keep working canvases under the iOS Safari canvas-area cap. Pure.
export const clampToArea = (width, height, maxArea) => {
  const area = width * height;
  if (area <= maxArea) {
    return { width: width, height: height };
  }
  const scale = Math.sqrt(maxArea / area);
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
  };
};

// Scale dimensions down so the longest edge <= maxDimension, preserving aspect
// ratio. Guards extreme aspect ratios (e.g. ultra-wide panoramas) that slip
// under the area cap but exceed a browser's max single-canvas dimension. Pure.
export const clampDimension = (width, height, maxDimension) => {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) {
    return { width: width, height: height };
  }
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
  };
};

// Given stored (pre-orientation) dimensions and an EXIF orientation, return
// the displayed dimensions. Orientations 5-8 are 90deg rotations that swap
// width and height. Pure.
export const orientedSize = (width, height, orientation) => {
  if (orientation >= 5 && orientation <= 8) {
    return { width: height, height: width };
  }
  return { width: width, height: height };
};

const setSmoothing = (ctx) => {
  ctx.imageSmoothingEnabled = true;
  // Not Baseline (absent on some engines) -- feature-detect before setting.
  if ('imageSmoothingQuality' in ctx) {
    ctx.imageSmoothingQuality = 'high';
  }
};

// Free a canvas's backing store immediately. Shrinking to 1x1 then clearing is
// the documented way to make iOS Safari reclaim canvas memory between stages.
// Source: https://pqina.nl/blog/total-canvas-memory-use-exceeds-the-maximum-limit/
const releaseCanvas = (canvas) => {
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, 1, 1);
  }
};

// Read the EXIF Orientation tag (1-8). Only JPEGs carry it; everything else --
// and any read failure -- resolves to 1 (upright). Uses @fengyuanchen/exif,
// matching the existing processImageConsideringOrientation usage in this file.
const readOrientation = (file) => {
  return new Promise((resolve) => {
    if (!file || file.type !== 'image/jpeg') {
      resolve(1);
      return;
    }
    try {
      new Exif(file, {
        interoperability: false,
        gps: false,
        exif: true,
        done: (tags) => {
          resolve(tags && tags.Orientation ? tags.Orientation : 1);
        },
        fail: () => {
          resolve(1);
        },
      });
    } catch (e) {
      resolve(1);
    }
  });
};

// Draw `source` (an Image or canvas, dimensions srcWidth x srcHeight in stored
// orientation) into a NEW canvas with the EXIF `orientation` applied at natural
// size (no scaling). Orientations 5-8 are 90deg rotations that swap width and
// height. Returns the upright canvas, or `source` unchanged when orientation is
// 1. Uses the standard EXIF orientation transform matrices.
const drawOrientedAtNaturalSize = (source, srcWidth, srcHeight, orientation) => {
  if (!orientation || orientation === 1) {
    return source;
  }
  const display = orientedSize(srcWidth, srcHeight, orientation);
  const canvas = document.createElement('canvas');
  canvas.width = display.width;
  canvas.height = display.height;
  const ctx = canvas.getContext('2d');
  setSmoothing(ctx);
  switch (orientation) {
    case 2: ctx.setTransform(-1, 0, 0, 1, srcWidth, 0); break;          // flip horizontal
    case 3: ctx.setTransform(-1, 0, 0, -1, srcWidth, srcHeight); break; // rotate 180
    case 4: ctx.setTransform(1, 0, 0, -1, 0, srcHeight); break;         // flip vertical
    case 5: ctx.setTransform(0, 1, 1, 0, 0, 0); break;                  // transpose
    case 6: ctx.setTransform(0, 1, -1, 0, srcHeight, 0); break;         // rotate 90 CW
    case 7: ctx.setTransform(0, -1, -1, 0, srcHeight, srcWidth); break; // transverse
    case 8: ctx.setTransform(0, -1, 1, 0, 0, srcWidth); break;          // rotate 90 CCW
    default: break;
  }
  ctx.drawImage(source, 0, 0, srcWidth, srcHeight);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return canvas;
};

// Progressively halve `source` (a canvas) toward the target, then a final exact
// draw. Halving blends neighbouring pixels at each step, avoiding the aliasing
// that a single large downscale produces. Each intermediate canvas is released
// so iOS Safari reclaims memory. Returns the final canvas; the caller exports it.
const stepDownCanvas = (source, srcWidth, srcHeight, dstWidth, dstHeight) => {
  let curWidth = srcWidth;
  let curHeight = srcHeight;
  let canvas = source;

  while (curWidth > dstWidth * 2 && curHeight > dstHeight * 2) {
    const nextWidth = Math.max(dstWidth, Math.floor(curWidth / 2));
    const nextHeight = Math.max(dstHeight, Math.floor(curHeight / 2));
    const next = document.createElement('canvas');
    next.width = nextWidth;
    next.height = nextHeight;
    const nextCtx = next.getContext('2d');
    setSmoothing(nextCtx);
    nextCtx.drawImage(canvas, 0, 0, curWidth, curHeight, 0, 0, nextWidth, nextHeight);
    releaseCanvas(canvas);
    canvas = next;
    curWidth = nextWidth;
    curHeight = nextHeight;
  }

  if (curWidth !== dstWidth || curHeight !== dstHeight) {
    const out = document.createElement('canvas');
    out.width = dstWidth;
    out.height = dstHeight;
    const outCtx = out.getContext('2d');
    setSmoothing(outCtx);
    outCtx.drawImage(canvas, 0, 0, curWidth, curHeight, 0, 0, dstWidth, dstHeight);
    releaseCanvas(canvas);
    canvas = out;
  }

  return canvas;
};

// Downscale an image File/Blob so its longest edge is <= opts.maxEdge,
// preserving aspect ratio and applying EXIF orientation. Never upscales. PNG
// sources stay PNG (lossless, preserves transparency and crisp text); every
// other type is exported as JPEG at opts.quality. Returns a Promise<Blob>.
// - If the source is already within bounds AND needs no reorientation, resolves
//   with the ORIGINAL file untouched (no needless re-encode -- avoids the
//   compression artifacts the Anthropic docs warn about).
// - If the source exceeds MAX_SOURCE_MEGAPIXELS, rejects with an Error whose
//   `code` is 'IMAGE_TOO_LARGE' so the caller can show a specific message.
// opts: { maxEdge: number, quality?: number (0..1, default 0.85) }
export const resizeImageToMaxEdge = (file, opts) => {
  const options = opts ? opts : {};
  const maxEdge = options.maxEdge;
  const quality =
    options.quality !== undefined && options.quality !== null
      ? options.quality
      : 0.85;
  const sourceType = file.type ? file.type : '';
  const outType = sourceType === 'image/png' ? 'image/png' : 'image/jpeg';

  // Read EXIF orientation first (JPEG only), then decode + resize.
  return readOrientation(file).then(function (orientation) {
    return new Promise((resolve, reject) => {
      const url = window.URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        const rawWidth = img.naturalWidth ? img.naturalWidth : img.width;
        const rawHeight = img.naturalHeight ? img.naturalHeight : img.height;

        // Decompression-bomb guard (runs after decode -- see MAX_SOURCE_MEGAPIXELS).
        const megapixels = (rawWidth * rawHeight) / 1000000;
        if (megapixels > MAX_SOURCE_MEGAPIXELS) {
          window.URL.revokeObjectURL(url);
          const tooLarge = new Error('Image exceeds the maximum supported resolution');
          tooLarge.code = 'IMAGE_TOO_LARGE';
          reject(tooLarge);
          return;
        }

        // Target is computed from the DISPLAYED (post-orientation) dimensions.
        const display = orientedSize(rawWidth, rawHeight, orientation);
        const dims = computeScaledDimensions(display.width, display.height, maxEdge);

        // Fast path: already small enough AND already upright -> send original.
        if (!dims.needsResize && (!orientation || orientation === 1)) {
          window.URL.revokeObjectURL(url);
          resolve(file);
          return;
        }

        let finalCanvas;
        try {
          // Stage 1: draw the decoded bitmap into a canvas clamped to the
          // browser's area + per-dimension limits (scaled, no rotation).
          const areaClamped = clampToArea(rawWidth, rawHeight, SAFE_CANVAS_AREA_PX);
          const clamped = clampDimension(
            areaClamped.width,
            areaClamped.height,
            MAX_CANVAS_DIMENSION_PX
          );
          const baseCanvas = document.createElement('canvas');
          baseCanvas.width = clamped.width;
          baseCanvas.height = clamped.height;
          const baseCtx = baseCanvas.getContext('2d');
          setSmoothing(baseCtx);
          baseCtx.drawImage(img, 0, 0, clamped.width, clamped.height);

          // Stage 2: apply EXIF orientation at natural size (rotation, no scale).
          const orientedCanvas = drawOrientedAtNaturalSize(
            baseCanvas,
            clamped.width,
            clamped.height,
            orientation
          );
          if (orientedCanvas !== baseCanvas) {
            releaseCanvas(baseCanvas);
          }

          // Stage 3: step-down halve to the final target dimensions.
          finalCanvas = stepDownCanvas(
            orientedCanvas,
            orientedCanvas.width,
            orientedCanvas.height,
            dims.width,
            dims.height
          );
        } catch (e) {
          window.URL.revokeObjectURL(url);
          reject(e);
          return;
        }

        const finish = (blob) => {
          window.URL.revokeObjectURL(url);
          releaseCanvas(finalCanvas);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Image resize failed: canvas produced no output'));
          }
        };

        if (finalCanvas.toBlob) {
          finalCanvas.toBlob(finish, outType, quality);
        } else {
          finish(null);
        }
      };

      img.onerror = () => {
        window.URL.revokeObjectURL(url);
        reject(new Error('Image resize failed: could not decode image'));
      };

      img.src = url;
    });
  });
};

export const getImageDataUrl = (file, callback) => {
  let url = window.URL.createObjectURL(file);
  var img = new Image();

  img.onload = () => {
    let width = img.width;
    let height = img.height;

    if (width > height) {
      if (width > MAX_WIDTH) {
        height *= MAX_WIDTH / width;
        width = MAX_WIDTH;
      }
    } else {
      if (height > MAX_HEIGHT) {
        width *= MAX_HEIGHT / height;
        height = MAX_HEIGHT;
      }
    }

    const canvas = document.getElementById('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg');
    callback(dataUrl, url);
  };
  img.src = url;
};
